import { I18n } from '@calendly/i18n';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { Sheet } from '@calendly/ui/components/sheet';
import { UiConfig } from '@calendly/ui/config';
import { useCloseOnEscape } from '@calendly/ui/hooks/use-close-on-escape';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { Layout, LayoutItem } from 'components/layout';
import { useCloseOnClickOutside } from '../hooks';

// Size-based breakpoints for UiConfig - automatically generates named queries
// like isPhone, isTabletUp, isDesktopUp for use with useDeviceType()
const SIDE_PANEL_BREAKPOINTS = {
  small: 500,
  medium: 850,
  large: 1100,
  xLarge: 1500
};
const defaultMinHeight = 400;
const defaultMaxHeight = 1000;
const defaultOffset = 16; // Default offset from the edges of the viewport

const useRelativePosition = (targetRef, children) => {
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const elementRef = useRef(null);
  const timeoutRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const isPhone = useDeviceType('isPhone');
  const [marginTop, setMarginTop] = useState(defaultOffset);
  const [maxHeight, setMaxHeight] = useState(defaultMaxHeight);
  const [isScrollable, setIsScrollable] = useState(true);
  useLayoutEffect(() => {
    if (isPhone || !elementRef.current) return () => {};
    const handleResize = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        const targetRect = targetRef?.current?.getBoundingClientRect();
        const elementRect = elementRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const headerHeight = headerRef.current?.scrollHeight;
        const contentHeight = contentRef.current?.scrollHeight;
        let newMarginTop = defaultOffset;
        if (targetRect) {
          const panelHeight = Math.min(elementRect.height, defaultMaxHeight);
          const panelBottom = Math.min(targetRect.bottom, viewportHeight - defaultOffset);
          newMarginTop = Math.max(panelBottom - panelHeight, defaultOffset);
        }

        // Adjust max height to fit in viewport
        const newMaxHeight = viewportHeight - newMarginTop - defaultOffset;
        setMarginTop(newMarginTop);
        setMaxHeight(newMaxHeight);
        if (headerHeight && contentHeight) {
          setIsScrollable(newMaxHeight <= contentHeight + headerHeight);
        }
      }, 100);
    };
    handleResize();

    // Set up ResizeObserver for content changes
    if (window.ResizeObserver && contentRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserverRef.current.observe(contentRef.current);
      if (headerRef.current) {
        resizeObserverRef.current.observe(headerRef.current);
      }
    }
    window.addEventListener('resize', handleResize);
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', handleResize);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isPhone, targetRef, children]);
  return {
    elementRef,
    contentRef,
    headerRef,
    isScrollable: isPhone ? true : isScrollable,
    marginTop: isPhone ? '0px' : `${marginTop}px`,
    maxHeight: isPhone ? '100vh' : `${maxHeight}px`
  };
};
const containerIsPhoneCss = "c1r0mi30";
const containerCss = "cqtz5hv";
const Container = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <FixedContainer ref={ref} {...props} className={classNames(containerCss, {
    [containerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const customSheetIsPhoneCss = "c1f4gv6r";
const customSheetCss = "c55trmm";
const CustomSheet = forwardRef((props, ref) => {
  const {
    minHeight,
    maxHeight,
    marginTop
  } = props;
  const isPhone = useDeviceType('isPhone');
  const style = {
    '--side-panel-default-height': `calc(100vh - ${defaultOffset * 2}px)`,
    '--side-panel-sheet-min-height': minHeight || `${defaultMinHeight}px`,
    '--side-panel-sheet-max-height': maxHeight || `${defaultMinHeight}px`,
    '--side-panel-sheet-margin-top': marginTop || `${defaultOffset}px`
  };
  return <Sheet ref={ref} {...props} style={style} className={classNames(customSheetCss, {
    [customSheetIsPhoneCss]: isPhone
  }, props.className)} />;
});
const headerWrapperCss = "h1vipns1";
const HeaderWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerWrapperCss, props.className)} />;
});
const Header = props => <Layout paddingY="12" paddingX="16" align="center" justify="space-between" {...props} />;
const bodyIsScrollableCss = "bnhogw9";
const bodyCss = "b1yq9od0";
const Body = forwardRef((props, ref) => {
  const {
    isScrollable,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyCss, {
    [bodyIsScrollableCss]: isScrollable
  }, props.className)} />;
});
const bodyContentCss = "b14rhtct";
const BodyContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyContentCss, props.className)} />;
});
const SidePanelContent = ({
  targetRef,
  header,
  minHeight,
  onClose,
  children
}) => {
  const {
    elementRef,
    contentRef,
    headerRef,
    isScrollable,
    marginTop,
    maxHeight
  } = useRelativePosition(targetRef, children);
  const {
    containerRef
  } = useCloseOnClickOutside(onClose);
  useCloseOnEscape({
    isOpen: true,
    onClose
  });
  return <Container ref={containerRef} data-component="side-panel">
      <CustomSheet ref={elementRef} minHeight={minHeight} maxHeight={maxHeight} marginTop={marginTop}>
        <HeaderWrapper ref={headerRef}>
          <Header onClose={onClose}>
            <LayoutItem>
              {header}
            </LayoutItem>
            <IconButton onClick={onClose} aria-label={I18n.t('publisher.action.close')} size="small">
              <CloseIcon />
            </IconButton>
          </Header>
        </HeaderWrapper>
        <Body isScrollable={isScrollable}>
          <BodyContent ref={contentRef}>
            {children}
          </BodyContent>
        </Body>
      </CustomSheet>
    </Container>;
};
export const SidePanel = props => <UiConfig breakpoints={SIDE_PANEL_BREAKPOINTS}>
    <SidePanelContent {...props} />
  </UiConfig>;

require("./side_panel.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./side_panel.js");